import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, MapPin, Landmark, Users, Plus, Pencil, Search,
  Loader2, CheckCircle, XCircle, Globe, Clock, ChevronRight
} from 'lucide-react';

const TABS = [
  { key: 'tenants', label: 'Tenants', icon: Building2 },
  { key: 'cities', label: 'Cities', icon: MapPin },
  { key: 'centres', label: 'Centres', icon: Landmark },
  { key: 'assignments', label: 'Assignments', icon: Users },
];

export default function OrgStructure() {
  const [tab, setTab] = useState('tenants');
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState({ tenants: [], cities: [], centres: [] });

  const loadSummary = useCallback(async () => {
    try {
      const [sumRes, metaRes] = await Promise.all([
        axios.get(`${API}/org/summary`),
        axios.get(`${API}/org/metadata`),
      ]);
      setSummary(sumRes.data);
      setMetadata(metaRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5" data-testid="org-structure-page">
        {/* Header */}
        <div>
          <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Organisation Structure</h1>
          <p className="text-xs text-adm-tertiary mt-1">Manage Tenant, City, Centre hierarchy</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="org-summary">
          {[
            { label: 'Tenants', value: summary.total_tenants, icon: Building2 },
            { label: 'Cities', value: summary.total_cities, icon: MapPin },
            { label: 'Centres', value: summary.total_centres, icon: Landmark },
            { label: 'Assignments', value: summary.active_assignments, icon: Users },
          ].map(s => (
            <Card key={s.label} className="bg-adm-surface border-white/[0.06]">
              <CardContent className="py-4 px-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-adm-accent/10 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-adm-accent" />
                </div>
                <div>
                  <div className="text-lg font-['Outfit'] font-semibold text-adm-primary">{s.value ?? '—'}</div>
                  <div className="text-[10px] text-adm-tertiary uppercase tracking-wider">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/[0.06] pb-0" data-testid="org-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-['Work_Sans'] border-b-2 transition-colors ${tab === t.key ? 'border-adm-accent text-adm-accent' : 'border-transparent text-adm-tertiary hover:text-adm-secondary'}`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'tenants' && <TenantsTab onRefresh={loadSummary} />}
        {tab === 'cities' && <CitiesTab metadata={metadata} onRefresh={loadSummary} />}
        {tab === 'centres' && <CentresTab metadata={metadata} onRefresh={loadSummary} />}
        {tab === 'assignments' && <AssignmentsTab metadata={metadata} onRefresh={loadSummary} />}
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════
// TENANTS TAB
// ═══════════════════════════════════════════════════════════

function TenantsTab({ onRefresh }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null); // null | 'create' | tenant obj for edit
  const [form, setForm] = useState({ name: '', code: '', domain: '', timezone_default: 'Asia/Kolkata' });

  const load = async () => {
    const res = await axios.get(`${API}/org/tenants`);
    setTenants(res.data?.tenants || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = tenants.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.name?.toLowerCase().includes(s) || t.code?.toLowerCase().includes(s);
  });

  const openCreate = () => {
    setForm({ name: '', code: '', domain: '', timezone_default: 'Asia/Kolkata' });
    setDialog('create');
  };

  const openEdit = (t) => {
    setForm({ name: t.name, code: t.code, domain: t.domain || '', timezone_default: t.timezone_default || 'Asia/Kolkata' });
    setDialog(t);
  };

  const handleSave = async () => {
    try {
      if (dialog === 'create') {
        await axios.post(`${API}/org/tenants`, form);
        toast.success('Tenant created');
      } else {
        await axios.put(`${API}/org/tenants/${dialog.tenant_id}`, form);
        toast.success('Tenant updated');
      }
      setDialog(null);
      load();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (t) => {
    await axios.put(`${API}/org/tenants/${t.tenant_id}`, { is_active: !t.is_active });
    toast.success(t.is_active ? 'Tenant deactivated' : 'Tenant activated');
    load();
    onRefresh();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="tenants-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-adm-tertiary" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenants..." className="pl-9 bg-white/[0.03] border-white/[0.08] text-adm-primary text-xs h-8" data-testid="tenant-search" />
        </div>
        <Button onClick={openCreate} className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 border border-adm-accent/20 text-xs h-8 px-3" data-testid="create-tenant-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Tenant
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-adm-tertiary text-xs">No tenants found</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => (
            <Card key={t.tenant_id} className="bg-adm-surface border-white/[0.06]">
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-adm-accent/10 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-adm-accent" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-['Outfit'] font-medium text-adm-primary">{t.name}</span>
                      <Badge className="text-[9px] bg-white/[0.06] text-adm-secondary border-0">{t.code}</Badge>
                      <Badge className={`text-[9px] border-0 ${t.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-adm-tertiary">
                      {t.domain && <span><Globe className="w-3 h-3 inline mr-0.5" />{t.domain}</span>}
                      <span><Clock className="w-3 h-3 inline mr-0.5" />{t.timezone_default}</span>
                      <span className="font-mono">{t.tenant_id}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="text-adm-tertiary hover:text-adm-primary h-7 px-2 text-xs" data-testid={`edit-tenant-${t.tenant_id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(t)} className={`h-7 px-2 text-xs ${t.is_active ? 'text-amber-400 hover:bg-amber-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}>
                    {t.is_active ? <XCircle className="w-3 h-3 mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                    {t.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-adm-panel border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-adm-primary font-['Outfit']">{dialog === 'create' ? 'Create Tenant' : 'Edit Tenant'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-adm-secondary mb-1 block">Name *</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="tenant-name" /></div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Code *</label><Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="e.g. REGENFLO" data-testid="tenant-code" /></div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Domain</label><Input value={form.domain} onChange={e => setForm(p => ({ ...p, domain: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="regenflo.care" data-testid="tenant-domain" /></div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Default Timezone</label><Input value={form.timezone_default} onChange={e => setForm(p => ({ ...p, timezone_default: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="tenant-tz" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-adm-secondary text-xs h-8">Cancel</Button>
            <Button onClick={handleSave} className="bg-adm-accent text-adm-bg hover:bg-adm-accent/90 text-xs h-8" data-testid="save-tenant-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CITIES TAB
// ═══════════════════════════════════════════════════════════

function CitiesTab({ metadata, onRefresh }) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ tenant_id: '', name: '', code: '', state: '', country: 'India', timezone: 'Asia/Kolkata' });

  const load = async () => {
    const params = tenantFilter !== 'all' ? `?tenant_id=${tenantFilter}` : '';
    const res = await axios.get(`${API}/org/cities${params}`);
    setCities(res.data?.cities || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [tenantFilter]);

  const filtered = cities.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.code?.toLowerCase().includes(s);
  });

  const openCreate = () => {
    const defaultTenant = metadata.tenants[0]?.tenant_id || '';
    setForm({ tenant_id: defaultTenant, name: '', code: '', state: '', country: 'India', timezone: 'Asia/Kolkata' });
    setDialog('create');
  };

  const openEdit = (c) => {
    setForm({ tenant_id: c.tenant_id, name: c.name, code: c.code, state: c.state || '', country: c.country || 'India', timezone: c.timezone || 'Asia/Kolkata' });
    setDialog(c);
  };

  const handleSave = async () => {
    try {
      if (dialog === 'create') {
        await axios.post(`${API}/org/cities`, form);
        toast.success('City created');
      } else {
        await axios.put(`${API}/org/cities/${dialog.city_id}`, form);
        toast.success('City updated');
      }
      setDialog(null);
      load();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="cities-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-48" data-testid="city-tenant-filter">
              <SelectValue placeholder="All tenants" />
            </SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-xs">All Tenants</SelectItem>
              {metadata.tenants.map(t => <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-xs">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-adm-tertiary" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cities..." className="pl-9 bg-white/[0.03] border-white/[0.08] text-adm-primary text-xs h-8 w-48" data-testid="city-search" />
          </div>
        </div>
        <Button onClick={openCreate} className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 border border-adm-accent/20 text-xs h-8 px-3" data-testid="create-city-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add City
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-adm-tertiary text-xs">No cities found</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <Card key={c.city_id} className="bg-adm-surface border-white/[0.06]">
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-['Outfit'] font-medium text-adm-primary">{c.name}</span>
                      <Badge className="text-[9px] bg-white/[0.06] text-adm-secondary border-0">{c.code}</Badge>
                      <Badge className={`text-[9px] border-0 ${c.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-adm-tertiary">
                      {c.state && <span>{c.state}, {c.country}</span>}
                      <span><Clock className="w-3 h-3 inline mr-0.5" />{c.timezone}</span>
                      <span><Landmark className="w-3 h-3 inline mr-0.5" />{c.centre_count ?? 0} centres</span>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="text-adm-tertiary hover:text-adm-primary h-7 px-2 text-xs" data-testid={`edit-city-${c.city_id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-adm-panel border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-adm-primary font-['Outfit']">{dialog === 'create' ? 'Create City' : 'Edit City'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {dialog === 'create' && (
              <div>
                <label className="text-xs text-adm-secondary mb-1 block">Tenant *</label>
                <Select value={form.tenant_id} onValueChange={v => setForm(p => ({ ...p, tenant_id: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="city-tenant-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-adm-panel border-white/10">
                    {metadata.tenants.map(t => <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-xs">{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><label className="text-xs text-adm-secondary mb-1 block">City Name *</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="city-name" /></div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Code *</label><Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="e.g. AHM" data-testid="city-code" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-adm-secondary mb-1 block">State</label><Input value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="Gujarat" data-testid="city-state" /></div>
              <div><label className="text-xs text-adm-secondary mb-1 block">Country</label><Input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="city-country" /></div>
            </div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Timezone</label><Input value={form.timezone} onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="city-tz" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-adm-secondary text-xs h-8">Cancel</Button>
            <Button onClick={handleSave} className="bg-adm-accent text-adm-bg hover:bg-adm-accent/90 text-xs h-8" data-testid="save-city-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CENTRES TAB
// ═══════════════════════════════════════════════════════════

function CentresTab({ metadata, onRefresh }) {
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ tenant_id: '', city_id: '', name: '', centre_code: '', address: '', timezone: 'Asia/Kolkata', services: '' });

  const load = async () => {
    const params = new URLSearchParams();
    if (tenantFilter !== 'all') params.set('tenant_id', tenantFilter);
    if (cityFilter !== 'all') params.set('city_id', cityFilter);
    const qs = params.toString() ? `?${params}` : '';
    const res = await axios.get(`${API}/org/centres${qs}`);
    setCentres(res.data?.centres || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [tenantFilter, cityFilter]);

  const filteredCities = metadata.cities.filter(c => tenantFilter === 'all' || c.tenant_id === tenantFilter);

  const filtered = centres.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.centre_code?.toLowerCase().includes(s);
  });

  const openCreate = () => {
    const dt = metadata.tenants[0]?.tenant_id || '';
    const dc = metadata.cities.filter(c => c.tenant_id === dt)[0]?.city_id || '';
    setForm({ tenant_id: dt, city_id: dc, name: '', centre_code: '', address: '', timezone: 'Asia/Kolkata', services: '' });
    setDialog('create');
  };

  const openEdit = (c) => {
    setForm({
      tenant_id: c.tenant_id || '', city_id: c.city_id || '',
      name: c.name, centre_code: c.centre_code || '',
      address: c.address || '', timezone: c.timezone || 'Asia/Kolkata',
      services: (c.services || []).join(', '),
    });
    setDialog(c);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, services: form.services.split(',').map(s => s.trim()).filter(Boolean) };
      if (dialog === 'create') {
        await axios.post(`${API}/org/centres`, payload);
        toast.success('Centre created');
      } else {
        await axios.put(`${API}/org/centres/${dialog.centre_id}`, payload);
        toast.success('Centre updated');
      }
      setDialog(null);
      load();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;

  const formCities = metadata.cities.filter(c => c.tenant_id === form.tenant_id);

  return (
    <div className="space-y-3" data-testid="centres-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={tenantFilter} onValueChange={v => { setTenantFilter(v); setCityFilter('all'); }}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-44">
              <SelectValue placeholder="All tenants" />
            </SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-xs">All Tenants</SelectItem>
              {metadata.tenants.map(t => <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-xs">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-40">
              <SelectValue placeholder="All cities" />
            </SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-xs">All Cities</SelectItem>
              {filteredCities.map(c => <SelectItem key={c.city_id} value={c.city_id} className="text-xs">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-adm-tertiary" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 bg-white/[0.03] border-white/[0.08] text-adm-primary text-xs h-8 w-40" />
          </div>
        </div>
        <Button onClick={openCreate} className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 border border-adm-accent/20 text-xs h-8 px-3" data-testid="create-centre-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Centre
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-adm-tertiary text-xs">No centres found</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <Card key={c.centre_id} className="bg-adm-surface border-white/[0.06]">
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Landmark className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-['Outfit'] font-medium text-adm-primary">{c.name}</span>
                      {c.centre_code && <Badge className="text-[9px] bg-white/[0.06] text-adm-secondary border-0">{c.centre_code}</Badge>}
                      <Badge className={`text-[9px] border-0 ${c.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-adm-tertiary">
                      <span><MapPin className="w-3 h-3 inline mr-0.5" />{c.city_name || c.city}</span>
                      <span><Users className="w-3 h-3 inline mr-0.5" />{c.active_assignments ?? 0} subscribers</span>
                      {c.address && <span>{c.address}</span>}
                    </div>
                    {(c.services || []).length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {c.services.map(s => <Badge key={s} className="text-[8px] bg-white/[0.04] text-adm-tertiary border-0 px-1.5">{s}</Badge>)}
                      </div>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="text-adm-tertiary hover:text-adm-primary h-7 px-2 text-xs" data-testid={`edit-centre-${c.centre_id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-adm-panel border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-adm-primary font-['Outfit']">{dialog === 'create' ? 'Create Centre' : 'Edit Centre'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {dialog === 'create' && (
              <>
                <div>
                  <label className="text-xs text-adm-secondary mb-1 block">Tenant *</label>
                  <Select value={form.tenant_id} onValueChange={v => setForm(p => ({ ...p, tenant_id: v, city_id: metadata.cities.filter(c => c.tenant_id === v)[0]?.city_id || '' }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-adm-panel border-white/10">
                      {metadata.tenants.map(t => <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-xs">{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-adm-secondary mb-1 block">City *</label>
                  <Select value={form.city_id} onValueChange={v => setForm(p => ({ ...p, city_id: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-adm-panel border-white/10">
                      {formCities.map(c => <SelectItem key={c.city_id} value={c.city_id} className="text-xs">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div><label className="text-xs text-adm-secondary mb-1 block">Centre Name *</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="centre-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-adm-secondary mb-1 block">Centre Code</label><Input value={form.centre_code} onChange={e => setForm(p => ({ ...p, centre_code: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="centre-code" /></div>
              <div><label className="text-xs text-adm-secondary mb-1 block">Timezone</label><Input value={form.timezone} onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" /></div>
            </div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Address</label><Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="centre-address" /></div>
            <div><label className="text-xs text-adm-secondary mb-1 block">Services (comma-separated)</label><Input value={form.services} onChange={e => setForm(p => ({ ...p, services: e.target.value }))} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="guided_session, assessment" data-testid="centre-services" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-adm-secondary text-xs h-8">Cancel</Button>
            <Button onClick={handleSave} className="bg-adm-accent text-adm-bg hover:bg-adm-accent/90 text-xs h-8" data-testid="save-centre-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ASSIGNMENTS TAB
// ═══════════════════════════════════════════════════════════

function AssignmentsTab({ metadata, onRefresh }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [centreFilter, setCentreFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialog, setDialog] = useState(null); // null | 'new' | assignment obj for reassign
  const [form, setForm] = useState({ subscriber_id: '', city_id: '', centre_id: '', primary_facilitator_id: '', secondary_facilitator_id: '' });

  const load = async () => {
    const params = new URLSearchParams();
    if (tenantFilter !== 'all') params.set('tenant_id', tenantFilter);
    if (cityFilter !== 'all') params.set('city_id', cityFilter);
    if (centreFilter !== 'all') params.set('centre_id', centreFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    const qs = params.toString() ? `?${params}` : '';
    const res = await axios.get(`${API}/org/assignments${qs}`);
    setAssignments(res.data?.assignments || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [tenantFilter, cityFilter, centreFilter, statusFilter]);

  const filteredCities = metadata.cities.filter(c => tenantFilter === 'all' || c.tenant_id === tenantFilter);
  const filteredCentres = metadata.centres.filter(c => (tenantFilter === 'all' || c.tenant_id === tenantFilter) && (cityFilter === 'all' || c.city_id === cityFilter));
  const formCentres = metadata.centres.filter(c => !form.city_id || c.city_id === form.city_id);
  const facilitators = metadata.facilitators || [];

  const openNew = () => {
    setForm({ subscriber_id: '', city_id: '', centre_id: '', primary_facilitator_id: '', secondary_facilitator_id: '' });
    setDialog('new');
  };

  const openReassign = (a) => {
    setForm({
      subscriber_id: a.subscriber_id,
      city_id: a.city_id || '',
      centre_id: a.centre_id || '',
      primary_facilitator_id: a.primary_facilitator_id || '',
      secondary_facilitator_id: a.secondary_facilitator_id || '',
    });
    setDialog(a);
  };

  const handleSave = async () => {
    if (!form.subscriber_id || !form.centre_id) { toast.error('Subscriber and centre are required'); return; }
    try {
      await axios.post(`${API}/terra/assignments`, {
        subscriber_id: form.subscriber_id,
        centre_id: form.centre_id,
        primary_facilitator_id: form.primary_facilitator_id || null,
        secondary_facilitator_id: form.secondary_facilitator_id || null,
      });
      toast.success(dialog === 'new' ? 'Subscriber assigned' : 'Subscriber reassigned');
      setDialog(null);
      load();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="assignments-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={tenantFilter} onValueChange={v => { setTenantFilter(v); setCityFilter('all'); setCentreFilter('all'); }}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-40"><SelectValue placeholder="All tenants" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-xs">All Tenants</SelectItem>
              {metadata.tenants.map(t => <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-xs">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={v => { setCityFilter(v); setCentreFilter('all'); }}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-36"><SelectValue placeholder="All cities" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-xs">All Cities</SelectItem>
              {filteredCities.map(c => <SelectItem key={c.city_id} value={c.city_id} className="text-xs">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={centreFilter} onValueChange={setCentreFilter}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-44"><SelectValue placeholder="All centres" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-xs">All Centres</SelectItem>
              {filteredCentres.map(c => <SelectItem key={c.centre_id} value={c.centre_id} className="text-xs">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-adm-secondary text-xs h-8 w-28"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="active" className="text-xs">Active</SelectItem>
              <SelectItem value="ended" className="text-xs">Ended</SelectItem>
              <SelectItem value="all" className="text-xs">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNew} className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 border border-adm-accent/20 text-xs h-8 px-3" data-testid="assign-subscriber-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Assign Subscriber
        </Button>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-12 text-adm-tertiary text-xs">No assignments found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {['Subscriber', 'Centre', 'City', 'Primary Facilitator', 'Status', 'Assigned By', 'Date', ''].map(h => (
                  <th key={h} className="py-2 px-3 text-left text-[10px] text-adm-tertiary uppercase tracking-wider font-['Work_Sans'] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assignments.map(a => (
                <tr key={a.assignment_id || a.subscriber_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="py-2.5 px-3">
                    <span className="text-adm-primary">{a.subscriber_name || '—'}</span>
                    {a.subscriber_email && <span className="text-adm-tertiary ml-1 text-[10px]">{a.subscriber_email}</span>}
                  </td>
                  <td className="py-2.5 px-3 text-adm-secondary">{a.centre_name || '—'}</td>
                  <td className="py-2.5 px-3 text-adm-secondary">{a.city_name || '—'}</td>
                  <td className="py-2.5 px-3 text-adm-secondary">{a.primary_facilitator_name || '—'}</td>
                  <td className="py-2.5 px-3">
                    <Badge className={`text-[9px] border-0 ${a.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>{a.status}</Badge>
                  </td>
                  <td className="py-2.5 px-3 text-adm-tertiary">{a.assigned_by_name || '—'}</td>
                  <td className="py-2.5 px-3 text-adm-tertiary">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                  <td className="py-2.5 px-3 text-right">
                    {a.status === 'active' && (
                      <Button variant="ghost" size="sm" onClick={() => openReassign(a)} className="text-adm-tertiary hover:text-adm-primary text-[10px] h-6 px-2" data-testid={`reassign-${a.assignment_id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Reassign
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign / Reassign Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-adm-panel border-white/10 max-w-md" data-testid="org-assign-dialog">
          <DialogHeader><DialogTitle className="text-adm-primary font-['Outfit']">{dialog === 'new' ? 'Assign Subscriber to Centre' : 'Reassign Subscriber'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {dialog === 'new' ? (
              <div>
                <label className="text-xs text-adm-secondary mb-1 block">Subscriber *</label>
                <Select value={form.subscriber_id} onValueChange={v => setForm(p => ({ ...p, subscriber_id: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="org-assign-subscriber"><SelectValue placeholder="Select subscriber" /></SelectTrigger>
                  <SelectContent className="bg-adm-panel border-white/10 max-h-60">
                    {(metadata.subscribers || []).map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name} ({s.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="p-2 rounded-lg bg-white/[0.03] text-xs text-adm-tertiary">
                Subscriber: <strong className="text-adm-primary">{dialog?.subscriber_name}</strong>
              </div>
            )}
            <div>
              <label className="text-xs text-adm-secondary mb-1 block">City</label>
              <Select value={form.city_id || '__all__'} onValueChange={v => setForm(p => ({ ...p, city_id: v === '__all__' ? '' : v, centre_id: '' }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs"><SelectValue placeholder="All cities" /></SelectTrigger>
                <SelectContent className="bg-adm-panel border-white/10">
                  <SelectItem value="__all__" className="text-xs">All Cities</SelectItem>
                  {metadata.cities.map(c => <SelectItem key={c.city_id} value={c.city_id} className="text-xs">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-adm-secondary mb-1 block">Centre *</label>
              <Select value={form.centre_id} onValueChange={v => setForm(p => ({ ...p, centre_id: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="org-assign-centre"><SelectValue placeholder="Select centre" /></SelectTrigger>
                <SelectContent className="bg-adm-panel border-white/10">
                  {formCentres.map(c => <SelectItem key={c.centre_id} value={c.centre_id} className="text-xs">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-adm-secondary mb-1 block">Primary Facilitator</label>
              <Select value={form.primary_facilitator_id || '__none__'} onValueChange={v => setForm(p => ({ ...p, primary_facilitator_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-adm-panel border-white/10">
                  <SelectItem value="__none__" className="text-xs">None</SelectItem>
                  {facilitators.map(f => <SelectItem key={f.id} value={f.id} className="text-xs">{f.name} ({f.role?.replace('_', ' ')})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-adm-secondary text-xs h-8">Cancel</Button>
            <Button onClick={handleSave} disabled={!form.centre_id || (dialog === 'new' && !form.subscriber_id)} className="bg-adm-accent text-adm-bg hover:bg-adm-accent/90 text-xs h-8" data-testid="org-save-assign-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
